import React, {useState} from 'react';

import FolderSelector, {DEFAULT_PROJECT_FOLDER_LABEL_TEXT} from 'file-browser/FolderSelector';
import FormInput from 'ui-library/bs/FormInput';
import {ButtonStyle} from 'ui-library/buttons/Button';
import FieldError from 'ui-library/FieldError';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import * as ModalContainer from 'ui-library/ModalContainer';
import Modal from 'ui-library/modals/Modal';
import {capitalize} from 'util/NameUtil';
import {GenericEvent, InputChangeEvent, InputFocusEvent} from 'util/Types';

const MAX_NAME_LENGTH = 255;

interface Props {
  additionalSubmitDisabled?: boolean;
  confirmButtonText: string;
  description?: string;
  initialFolderId?: string;
  readableItemType: string;
  namePlaceholder?: string;
  onConfirm: ({folderId, name}: {folderId?: string; name: string}) => void;
  onRequestClose: () => void;
  title: string;
  children?: React.ReactNode;
}

function CreateComponentModal(props: Props): JSX.Element {
  const [folderId, setFolderId] = useState<string | undefined>(props.initialFolderId);
  const [name, setName] = useState<string>(props.namePlaceholder ?? '');

  const handleSelectFolder = (e: GenericEvent): void => {
    setFolderId(e.target.value);
  };

  const handleConfirm = (): void => {
    props.onConfirm({folderId, name});
    props.onRequestClose();
  };

  const handleChangeName = (e: InputChangeEvent): void => {
    setName(e.target.value);
  };

  const handleOnFocus = (e: InputFocusEvent): void => {
    const input = e.target;
    input.setSelectionRange(0, input.value.length);
  };

  const nameOrFolderMissing = !folderId || name.trim().length === 0;
  const nameIsTooLong = name.length > MAX_NAME_LENGTH;
  const errorMessage = nameIsTooLong
    ? `Names can be at most 255 characters long (currently ${name.length})`
    : undefined;

  return (
    <Modal
      title={props.title}
      onRequestClose={props.onRequestClose}
      buttons={[
        {
          text: props.confirmButtonText,
          onClick: handleConfirm,
          style: ButtonStyle.SECONDARY,
          disabled: nameOrFolderMissing || (props.additionalSubmitDisabled ?? false) || nameIsTooLong,
        },
      ]}
    >
      {props.description && <div className="u-small-padding-bottom">{props.description}</div>}

      <div className="form-group">
        <FieldLabel
          label={DEFAULT_PROJECT_FOLDER_LABEL_TEXT}
          isRequired={true}
          htmlFor="create-component-folder-selector"
        />
        <FolderSelector
          enableRedesign={true}
          showInventory={true}
          value={folderId}
          onChange={handleSelectFolder}
          unsetOnFetchFail={true}
          allowNone={false}
          id="create-component-folder-selector"
        />
      </div>

      <div className="form-group">
        <FieldLabel
          label={`${capitalize(props.readableItemType)} name`}
          isRequired={true}
          htmlFor="create-component-name-input"
        />
        <FormInput
          focusOnMount={true}
          onFocus={handleOnFocus}
          placeholder={props.namePlaceholder ?? 'Name'}
          onChange={handleChangeName}
          type="text"
          value={name}
          dataTestAttrs={{component: 'CreateComponentModal', element: 'nameInput'}}
          error={errorMessage}
          id="create-component-name-input"
          aria-label={`${capitalize(props.readableItemType)} name`}
        />
        <FieldError message={errorMessage ?? null} />
      </div>
      {props.children}
    </Modal>
  );
}

CreateComponentModal.open = (props: Props): void => {
  const modalProps = {
    ...props,
    onRequestClose: () => {
      ModalContainer.close(CreateComponentModal);
    },
  };
  ModalContainer.open(CreateComponentModal, React.createElement(CreateComponentModal, modalProps));
};

export default CreateComponentModal;
